import { type RefObject, useCallback, useEffect, useState } from "react";
import { getStickyHeaderHeight } from "~/components/utils/stuck";
import { VIEWPORT_MD } from "~/constants";
import { useWindowDimensions } from "./window-width";

export function useStuck(
  ref: RefObject<HTMLElement | null>,
  options?: {
    offset?: number;
    desktopStuck?: boolean;
    excludeHeader?: boolean;
  },
) {
  const [stuck, setStuck] = useState(false);

  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const scrolled = useScrolled();

  // This is used to calculate if the category is pinned to the top so that we can optionally show a box shadow when scrolled
  useEffect(() => {
    if (options?.desktopStuck && windowWidth >= VIEWPORT_MD) {
      // On desktop width the component will always be "stuck" so base it on being scrolled
      setStuck(scrolled);
      return;
    }

    const currentRef = ref.current; // Cache here so that it can properly be cleaned up
    if (currentRef) {
      const rootMarginTop =
        0 -
        ((options?.excludeHeader ? 0 : getStickyHeaderHeight()) +
          (options?.offset || 0) +
          1); // additional pixel to lower the intersect boundary below the sticky headers

      const observer = new IntersectionObserver(
        ([e]) => {
          setStuck(
            e.intersectionRatio < 1 &&
              currentRef.getBoundingClientRect().bottom <= windowHeight,
          );
        },
        {
          root: null,
          threshold: 1,
          rootMargin: `${rootMarginTop}px 0px 0px 0px`,
        },
      );
      observer.observe(currentRef);
      return () => observer.unobserve(currentRef);
    }
  }, [
    ref,
    windowWidth,
    windowHeight,
    scrolled,
    options?.desktopStuck,
    options?.offset,
    options?.excludeHeader,
  ]);

  return stuck;
}

const isScrolled = () => {
  return window.scrollY > 0;
};

function useScrolled() {
  const [scrolled, setScrolled] = useState<boolean>(isScrolled());

  const onScroll = useCallback(() => {
    setScrolled(isScrolled());
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll);

    return () => {
      document.removeEventListener("scroll", onScroll);
    };
  }, [onScroll]);

  return scrolled;
}
